import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Plus, Check, Loader2 } from "lucide-react";
import { streamAI, parseSuggestions, getSessionId } from "@/lib/ai";
import { SERVICES, serviceTitle } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HELLO = { role: "assistant", content: "Ciao! Sono l'assistente di Mister Multiservice. Dimmi che spazio devi pulire e ti consiglio i servizi giusti." };
const VALID = new Set(SERVICES.map((s) => s.id));

const Bubble = ({ m, selected, onAdd, i }) => {
  const { clean, ids } = parseSuggestions(m.content);
  const valid = ids.filter((id) => VALID.has(id));
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${mine ? "bg-[var(--cyan)] text-black" : "bg-white/[0.06] text-slate-100"}`}>
        <p className="whitespace-pre-wrap">{clean || <Loader2 className="h-4 w-4 animate-spin" />}</p>
        {valid.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {valid.map((id) => {
              const on = selected.includes(id);
              return (
                <button key={id} disabled={on} onClick={() => onAdd(id)} data-testid={`chat-add-${id}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "border-[var(--cyan)] text-[var(--cyan)]" : "border-white/25 text-white hover:border-[var(--cyan)]"}`}>
                  {on ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />} {serviceTitle(id)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const ChatWidget = ({ selected, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const session = useRef(null);

  useEffect(() => {
    if (!open || session.current) return;
    session.current = getSessionId();
    axios.get(`${API}/chat/history`, { params: { session_id: session.current } })
      .then((r) => setMsgs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMsgs([]));
  }, [open]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const setLast = (content) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content }]);
    try {
      await streamAI("/chat/stream", { session_id: session.current, message: q }, setLast);
    } catch (err) {
      setLast(err.message || "Errore. Riprova o scrivici su WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="chat-toggle-button" aria-label="Assistente AI"
        className="fixed bottom-24 right-4 z-50 grid h-14 w-14 place-items-center rounded-full bg-white text-black shadow-[0_15px_40px_-10px_rgba(0,229,255,0.6)] transition-transform duration-300 hover:scale-105 active:scale-95 sm:bottom-6 sm:right-6">
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} data-testid="chat-panel" data-lenis-prevent
            className="fixed inset-x-3 bottom-40 top-24 z-50 flex flex-col overflow-hidden rounded-[28px] border border-white/10 bg-[#0A1020]/95 backdrop-blur-xl sm:inset-x-auto sm:bottom-24 sm:right-6 sm:top-auto sm:h-[560px] sm:w-[400px]">
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--cyan)] text-black"><Sparkles className="h-4 w-4" /></span>
              <div>
                <p className="font-display text-base font-semibold text-white">Assistente Mister</p>
                <p className="text-xs text-slate-400">Risposte automatiche AI · il preventivo resta gratuito</p>
              </div>
            </div>
            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
              {[HELLO, ...msgs].map((m, i) => <Bubble key={i} i={i} m={m} selected={selected} onAdd={onAdd} />)}
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-white/10 p-3">
              <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Es. ufficio di 200 mq a Monza…" data-testid="chat-input" className="input-dark !rounded-full !py-3" />
              <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send-button" aria-label="Invia" className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--cyan)] text-black disabled:opacity-50">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
